import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, Loader2, Paperclip, Send, FileText, Image as ImageIcon, CheckCircle2, Gavel } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const inp = "w-full px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// mode: "auth" (référent/propriétaire connecté) | "public" (locataire via lien)
export const EdlDispute = ({ mode = "auth", eid = "", token = "", canResolve = false, authorName = "" }) => {
  const base = mode === "public" ? `/edl/public/view/${token}/dispute` : `/edl/${eid}/dispute`;
  const [data, setData] = useState({ comments: [], annexes: [], recommendation: null });
  const [text, setText] = useState("");
  const [decision, setDecision] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get(base).then((r) => { setData(r.data || { comments: [], annexes: [] }); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eid, token]);
  const resolved = data.status === "resolved";

  const resolve = async () => {
    if (!decision.trim()) { toast.error("Saisissez la décision finale"); return; }
    setBusy("res");
    try { await api.put(`/edl/${eid}/dispute/resolve`, { decision }); toast.success("Litige clôturé"); setDecision(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  };
  const reopen = async () => {
    setBusy("reo");
    try { await api.put(`/edl/${eid}/dispute/reopen`); toast.success("Litige rouvert"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  };
  const journalPdf = async () => {
    try { const res = await api.get(`/edl/${eid}/dispute/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "journal-litige.pdf"; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("PDF indisponible"); }
  };

  const send = async () => {
    if (!text.trim()) return;
    setBusy("c");
    try { await api.post(`${base}/comment`, mode === "public" ? { body: text, author_name: authorName } : { body: text }); setText(""); load(); }
    catch { toast.error("Envoi impossible"); } finally { setBusy(""); }
  };
  const upload = async (f) => {
    if (!f) return; setBusy("a");
    try {
      const fd = new FormData(); fd.append("file", f);
      if (mode === "public") { fd.append("author_name", authorName || ""); }
      const url = mode === "public" ? `/edl/public/view/${token}/dispute/annex` : `/edl/${eid}/dispute/annex`;
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pièce jointe ajoutée au litige"); load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  };

  return (
    <div className="bg-white rounded-3xl border border-brand-soft p-5 space-y-4" data-testid="edl-dispute">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><MessageSquare className="w-4 h-4" /></div>
        <h3 className="font-display text-lg font-semibold text-brand-greenDeep">Espace litige & échanges</h3>
        <div className="ml-auto flex items-center gap-2">
          {resolved && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="dispute-resolved-badge">Litige résolu</span>}
          {mode === "auth" && <button onClick={journalPdf} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-brand-soft text-brand-greenDeep text-xs font-semibold hover:bg-brand-softer" data-testid="dispute-journal-pdf"><FileText className="w-3.5 h-3.5" /> Journal PDF</button>}
        </div>
      </div>

      {resolved && data.resolution && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3" data-testid="dispute-resolution">
          <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5 mb-1"><CheckCircle2 className="w-3.5 h-3.5" /> Décision finale du référent</p>
          <p className="text-sm text-emerald-900 whitespace-pre-wrap">{data.resolution.decision}<span className="block text-[11px] text-emerald-700/70 mt-1">— {data.resolution.by} ({(data.resolution.at || "").slice(0, 10)})</span></p>
          {canResolve && <button onClick={reopen} disabled={busy === "reo"} className="mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-emerald-300 text-emerald-800 text-xs font-semibold disabled:opacity-60" data-testid="dispute-reopen-btn">{busy === "reo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gavel className="w-3.5 h-3.5" />} Rouvrir le litige</button>}
        </div>
      )}

      <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="dispute-comments">
        {(data.comments || []).length === 0 && <p className="text-xs text-brand-green/40">Aucun échange pour le moment.</p>}
        {(data.comments || []).map((c) => (
          <div key={c.id} className="rounded-2xl bg-brand-softer p-3" data-testid={`dispute-comment-${c.id}`}>
            <div className="flex items-center justify-between"><span className="text-xs font-semibold text-brand-greenDeep">{c.author_name} <span className="text-[10px] font-normal text-brand-green/45">· {c.author_role}</span></span><span className="text-[10px] text-brand-green/40">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span></div>
            <p className="text-sm text-brand-green/80 mt-0.5 whitespace-pre-wrap">{c.body}</p>
          </div>
        ))}
      </div>

      {(data.annexes || []).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="dispute-annexes">
          {(data.annexes || []).map((a) => (
            <a key={a.id} href={`${BACKEND}${a.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-soft text-brand-greenDeep text-xs font-semibold hover:bg-brand-softer" data-testid={`dispute-annex-${a.id}`}>
              {a.kind === "pdf" ? <FileText className="w-3.5 h-3.5" /> : <ImageIcon className="w-3.5 h-3.5" />} {a.name}
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={mode === "public" ? "Déclarer un litige ou ajouter un message…" : "Ajouter un commentaire…"} className={inp} data-testid="dispute-comment-input" />
        <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="dispute-annex-input" />
        <button onClick={() => fileRef.current?.click()} disabled={busy === "a"} className="p-2.5 rounded-xl border border-brand-soft text-brand-greenDeep hover:bg-brand-softer" title="Joindre une pièce (photo / PDF)" data-testid="dispute-annex-btn">{busy === "a" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}</button>
        <button onClick={send} disabled={busy === "c"} className="px-4 rounded-xl bg-brand-greenDeep text-white disabled:opacity-60" data-testid="dispute-send-btn">{busy === "c" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>

      {canResolve && !resolved && (
        <div className="border-t border-brand-soft pt-3" data-testid="dispute-resolve-box">
          <p className="text-xs font-semibold text-brand-greenDeep mb-1.5 flex items-center gap-1.5"><Gavel className="w-3.5 h-3.5" /> Clôturer le litige avec une décision finale</p>
          <textarea value={decision} onChange={(e) => setDecision(e.target.value)} rows={2} className={inp} placeholder="Décision finale (répartition, retenue sur dépôt, conciliation…) — visible par toutes les parties" data-testid="dispute-resolve-input" />
          <button onClick={resolve} disabled={busy === "res"} className="mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60" data-testid="dispute-resolve-btn">{busy === "res" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Marquer le litige comme résolu</button>
        </div>
      )}
    </div>
  );
};

export default EdlDispute;
